import React, { useMemo, useState, useEffect } from "react";
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import {
    ExpandMore,
    TrendingUp,
    TrendingDown,
    AccountBalanceWallet,
    AttachMoney,
    ShowChart,
    WarningAmber,
    CheckCircle,
    Description,
    Business,
    CalendarMonth,
    Assessment,
    InfoOutlined,
    AssessmentRounded,
    ErrorOutline
} from "@mui/icons-material";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";
import HeaderDashboard from "../../components/HeaderDashboard";
import axios from "axios";


const PURPLE = "#9C01B9";
const GREEN = "#1EFF86";
const DARK = "#151515";
const MUTED = "#667085";
const BORDER = "#E8E8EE";

const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }

    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL"
    }).format(Number(value));
};

const formatPercent = (value) => {
    if (
        value === null ||
        value === undefined ||
        Number.isNaN(Number(value))
    ) {
        return "—";
    }

    return `${Number(value).toFixed(1).replace(".", ",")}%`;
};

const formatCompetencia = (competencia) => {
    if (!competencia) return "—";

    const [ano, mes] = competencia.split("-");

    const meses = [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro"
    ];

    return `${meses[Number(mes) - 1] || mes}/${ano}`;
};

const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleString("pt-BR");
};

const getVariationColor = (value) => {
    if (value > 0) return "#087A3D";
    if (value < 0) return "#C62828";
    return MUTED;
};

const getVariationIcon = (value) => {
    if (value > 0) {
        return <TrendingUp sx={{ fontSize: 18 }} />;
    }

    if (value < 0) {
        return <TrendingDown sx={{ fontSize: 18 }} />;
    }

    return null;
};

const translateConcept = (concept) => {
    const map = {
        RECEITA_BRUTA: "Receita Bruta",
        DEDUCOES_RECEITA: "Deduções",
        RECEITA_LIQUIDA: "Receita Líquida",
        LUCRO_BRUTO: "Lucro Bruto",
        DESPESAS_OPERACIONAIS: "Despesas Operacionais",
        RESULTADO_OPERACIONAL: "Resultado Operacional",
        LUCRO_LIQUIDO: "Lucro Líquido",
        ATIVO: "Ativo",
        PASSIVO_TOTAL: "Passivo Total",
        PATRIMONIO_LIQUIDO: "Patrimônio Líquido",
        ATIVO_CIRCULANTE: "Ativo Circulante",
        PASSIVO_CIRCULANTE: "Passivo Circulante",
        CAIXA_EQUIVALENTES: "Caixa e Equivalentes",
        BANCOS: "Bancos",
        APLICACOES_FINANCEIRAS: "Aplicações Financeiras",
        CONTAS_RECEBER: "Contas a Receber",
        OBRIGACOES_TRIBUTARIAS: "Obrigações Tributárias",
        OBRIGACOES_TRABALHISTAS: "Obrigações Trabalhistas",
        CAPITAL_SOCIAL: "Capital Social",
        LUCROS_PREJUIZOS_ACUMULADOS: "Lucros / Prejuízos Acumulados",
        OUTRAS_DESPESAS: "Outras Despesas",
        DESPESAS_PESSOAL: "Despesas com Pessoal"
    };

    return (
        map[concept] ||
        concept
            ?.replaceAll("_", " ")
            ?.toLowerCase()
            ?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
        "—"
    );
};

const SectionTitle = ({
    icon,
    title,
    subtitle
}) => {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        background: `${PURPLE}12`,
                        color: PURPLE,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    {icon}
                </Box>

                <Box>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "18px",
                            color: DARK
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            sx={{
                                color: MUTED,
                                fontSize: "13px",
                                mt: 0.2
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

const MetricCard = ({
    title,
    value,
    subtitle,
    icon,
    accent = PURPLE,
    variation
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                height: "100%",
                border: `1px solid ${BORDER}`,
                borderRadius: "16px",
                p: 2.3,
                background: "#fff",
                transition: "all .2s ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,.06)"
                }
            }}
        >
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Box>
                    <Typography
                        sx={{
                            color: MUTED,
                            fontSize: "13px",
                            fontWeight: 600
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "25px",
                            fontWeight: 800,
                            color: DARK,
                            mt: 0.8
                        }}
                    >
                        {value}
                    </Typography>

                    {subtitle && (
                        <Typography
                            sx={{
                                color: MUTED,
                                fontSize: "12px",
                                mt: 0.5
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        background: `${accent}12`,
                        color: accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    {icon}
                </Box>
            </Stack>

            {variation !== undefined && variation !== null && (
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                        mt: 1.5,
                        color: getVariationColor(variation),
                        fontSize: "12px",
                        fontWeight: 700
                    }}
                >
                    {getVariationIcon(variation)}
                    {formatPercent(Math.abs(variation))}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "12px",
                            color: MUTED,
                            fontWeight: 500
                        }}
                    >
                        no período
                    </Typography>
                </Stack>
            )}
        </Paper>
    );
};

const StatusChip = ({ status }) => {
    const configs = {
        ok: {
            label: "Conferido",
            color: "#087A3D",
            bg: "#EAF8F0",
            icon: <CheckCircle sx={{ fontSize: 15 }} />
        },
        nao_aplicavel: {
            label: "Não aplicável",
            color: "#667085",
            bg: "#F2F4F7",
            icon: <InfoOutlined sx={{ fontSize: 15 }} />
        }
    };

    const config = configs[status] || configs.nao_aplicavel;

    return (
        <Chip
            size="small"
            icon={config.icon}
            label={config.label}
            sx={{
                color: config.color,
                backgroundColor: config.bg,
                fontWeight: 700,
                borderRadius: "7px"
            }}
        />
    );
};

export default function AnaliseContabil() {
    const [analise, setAnalise] = useState({});

    useEffect(() => {
        const getAnalise = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/analise-contabil`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": localStorage.getItem("token")
                    }
                });
                setAnalise(response.data.data.analise);
            } catch (error) {
                console.error("Erro ao obter análise contábil:", error);
            }
        };
        getAnalise();
    }, []);

    const dados = analise?.dados || {};
    const periodo = analise?.periodo || {};
    const empresa = analise?.empresa || {};
    const capacidade = analise?.capacidadeAnalise || {};
    const conferencias = analise?.analises?.conferencias || analise?.conferencias || [];
    const receita = dados.receita || {};
    const resultado = dados.resultado || {};
    const despesas = dados.despesas || {};
    const balanco = dados.balanco || {};

    const evolucao = useMemo(() => {
        return (dados.evolucaoMensal || []).map((item) => ({
            ...item,
            mes: formatCompetencia(item.competencia)
        }));
    }, [dados.evolucaoMensal]);

    const alertas = conferencias.alertas || [];

    const totalVerificacoes =
        conferencias.resumo?.verificacoesOk || 0;

    const divergencias =
        conferencias.resumo?.verificacoesDivergentes || 0;

    const cobertura =
        periodo.mesesSolicitados > 0
            ? (periodo.mesesDisponiveis / periodo.mesesSolicitados) * 100
            : 0;

    return (
        <>
            <HeaderDashboard />
            {analise?.dados ? (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: "1400px",
                        mx: "auto",
                        py: { xs: 2, md: 4 },
                        px: { xs: 1.5, md: 3 },
                    }}
                >
                    {/* HEADER */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: "20px",
                            border: `1px solid ${BORDER}`,
                            overflow: "hidden",
                            mb: 2.5,
                            mt: 6,
                        }}
                    >
                        <Box
                            sx={{
                                p: { xs: 2.5, md: 3.5 },
                                background:
                                    "linear-gradient(135deg, #ffffff 0%, #fbf7fc 100%)"
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ mb: 1 }}
                                    >
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                background: GREEN,
                                                boxShadow: `0 0 0 5px ${GREEN}20`
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 800,
                                                color: "#087A3D",
                                                textTransform: "uppercase",
                                                letterSpacing: ".5px"
                                            }}
                                        >
                                            Análise financeira
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: "23px",
                                                md: "30px"
                                            },
                                            fontWeight: 850,
                                            color: DARK,
                                            lineHeight: 1.15
                                        }}
                                    >
                                        {empresa.razaoSocial}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.8,
                                            color: MUTED,
                                            fontSize: "14px"
                                        }}
                                    >
                                        CNPJ: {empresa.cnpj}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            border: `1px solid ${BORDER}`,
                                            borderRadius: "14px",
                                            p: 1.5,
                                            minWidth: { xs: "100%", md: 220 },
                                            background: "#fff"
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "11px",
                                                color: MUTED,
                                                fontWeight: 700
                                            }}
                                        >
                                            PERÍODO ANALISADO
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "16px",
                                                mt: 0.5
                                            }}
                                        >
                                            {formatCompetencia(periodo.inicio)}
                                            {" — "}
                                            {formatCompetencia(periodo.fim)}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: MUTED,
                                                fontSize: "12px",
                                                mt: 0.3
                                            }}
                                        >
                                            {periodo.mesesDisponiveis} de{" "}
                                            {periodo.mesesSolicitados} meses disponíveis
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Paper>

                    {/* COBERTURA */}
                    {periodo.competenciasAusentes?.length > 0 && (
                        <Alert
                            severity="warning"
                            icon={<WarningAmber />}
                            sx={{
                                mb: 2.5,
                                borderRadius: "14px",
                                border: "1px solid #F5D98B",
                                alignItems: "flex-start"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "14px",
                                    mb: 0.3
                                }}
                            >
                                Análise com histórico incompleto
                            </Typography>

                            <Typography sx={{ fontSize: "13px" }}>
                                Foram solicitados {periodo.mesesSolicitados} meses,
                                porém existem dados disponíveis para{" "}
                                {periodo.mesesDisponiveis}. A competência{" "}
                                {periodo.competenciasAusentes
                                    .map(formatCompetencia)
                                    .join(", ")}{" "}
                                não está disponível.
                            </Typography>

                            <Box sx={{ mt: 1.5 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={cobertura}
                                    sx={{
                                        height: 6,
                                        borderRadius: 5,
                                        background: "#F1E8CC",
                                        "& .MuiLinearProgress-bar": {
                                            background: "#D99A00"
                                        }
                                    }}
                                />
                            </Box>
                        </Alert>
                    )}

                    {/* INDICADORES PRINCIPAIS */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 3,
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    lg: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >
                            <MetricCard
                                title="Receita líquida"
                                value={formatCurrency(receita.liquida)}
                                subtitle="Total no período"
                                icon={<AttachMoney />}
                                accent={PURPLE}
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    lg: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >
                            <MetricCard
                                title="Lucro líquido"
                                value={formatCurrency(resultado.lucroLiquido)}
                                subtitle="Resultado acumulado"
                                icon={<TrendingUp />}
                                accent="#087A3D"
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    lg: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >
                            <MetricCard
                                title="Margem líquida"
                                value={formatPercent(
                                    resultado.margemLiquida?.valor
                                )}
                                subtitle="Lucro / receita líquida"
                                icon={<ShowChart />}
                                accent="#0B7A75"
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    lg: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >
                            <MetricCard
                                title="Patrimônio líquido"
                                value={formatCurrency(
                                    balanco.posicaoFinal?.patrimonioLiquido
                                )}
                                subtitle="Última posição disponível"
                                icon={<AccountBalanceWallet />}
                                accent="#5E35B1"
                            />
                        </Box>
                    </Box>

                    {/* EVOLUÇÃO */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`
                        }}
                    >
                        <SectionTitle
                            icon={<ShowChart />}
                            title="Evolução financeira"
                            subtitle="Comportamento da receita e do resultado nos meses disponíveis"
                        />

                        <Box sx={{ width: "100%", height: 340 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={evolucao}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#EEEEF2"
                                    />

                                    <XAxis
                                        dataKey="mes"
                                        tick={{
                                            fontSize: 11,
                                            fill: MUTED
                                        }}
                                    />

                                    <YAxis
                                        tick={{
                                            fontSize: 11,
                                            fill: MUTED
                                        }}
                                        tickFormatter={(value) =>
                                            `R$ ${Math.round(
                                                value / 1000
                                            )}k`
                                        }
                                    />

                                    <Tooltip
                                        formatter={(value) =>
                                            formatCurrency(value)
                                        }
                                    />

                                    <Legend />

                                    <Line
                                        type="monotone"
                                        dataKey="receitaLiquida"
                                        name="Receita líquida"
                                        stroke={PURPLE}
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                    />

                                    <Line
                                        type="monotone"
                                        dataKey="lucroLiquido"
                                        name="Lucro líquido"
                                        stroke="#087A3D"
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>

                    {/* INDICADORES */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 3,
                            width: "100%",
                        }}
                    >
                        {[
                            {
                                title: "Margem bruta",
                                value: formatPercent(
                                    resultado.margemBruta?.valor
                                ),
                                description: "Lucro bruto / Receita líquida",
                                color: PURPLE,
                            },
                            {
                                title: "Margem operacional",
                                value: formatPercent(
                                    resultado.margemOperacional?.valor
                                ),
                                description:
                                    "Resultado operacional / Receita líquida",
                                color: "#087A3D",
                            },
                            {
                                title: "Crescimento da receita",
                                value: formatPercent(
                                    receita.crescimentoInicioFim
                                        ?.variacaoPercentual
                                ),
                                description:
                                    "Do primeiro ao último mês disponível",
                                color: getVariationColor(
                                    receita.crescimentoInicioFim
                                        ?.variacaoPercentual
                                ),
                            },
                        ].map((item) => (
                            <Box
                                key={item.title}
                                sx={{
                                    flex: {
                                        xs: "1 1 100%",
                                        md: "1 1 calc(33.333% - 11px)",
                                    },
                                    minWidth: 0,
                                    display: "flex",
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: "16px",
                                        border: `1px solid ${BORDER}`,
                                        width: "100%",
                                        height: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: "15px",
                                            mb: 2,
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "28px",
                                            fontWeight: 850,
                                            color: item.color,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {item.value}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: MUTED,
                                            mt: 0.5,
                                        }}
                                    >
                                        {item.description}
                                    </Typography>
                                </Paper>
                            </Box>
                        ))}
                    </Box>

                    {/* DRE */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`
                        }}
                    >
                        <SectionTitle
                            icon={<Assessment />}
                            title="Resultado da operação"
                            subtitle="Principais componentes da demonstração do resultado"
                        />

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>
                                            Indicador
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            Valor
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {[
                                        ["Receita bruta", receita.bruta],
                                        ["Deduções", receita.deducoes],
                                        ["Receita líquida", receita.liquida],
                                        [
                                            "Lucro bruto",
                                            resultado.lucroBruto
                                        ],
                                        [
                                            "Despesas operacionais",
                                            dados.evolucaoMensal?.reduce(
                                                (total, item) =>
                                                    total +
                                                    Math.abs(
                                                        item.despesasOperacionais || 0
                                                    ),
                                                0
                                            )
                                        ],
                                        [
                                            "Resultado operacional",
                                            resultado.resultadoOperacional
                                        ],
                                        [
                                            "Lucro líquido",
                                            resultado.lucroLiquido
                                        ]
                                    ].map(([label, value]) => (
                                        <TableRow key={label}>
                                            <TableCell
                                                sx={{
                                                    fontWeight:
                                                        label ===
                                                            "Lucro líquido" ||
                                                            label ===
                                                            "Receita líquida"
                                                            ? 800
                                                            : 500
                                                }}
                                            >
                                                {label}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight:
                                                        label ===
                                                            "Lucro líquido" ||
                                                            label ===
                                                            "Receita líquida"
                                                            ? 800
                                                            : 500,
                                                    color:
                                                        value < 0
                                                            ? "#C62828"
                                                            : DARK
                                                }}
                                            >
                                                {formatCurrency(value)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* BALANÇO */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`
                        }}
                    >
                        <SectionTitle
                            icon={<AccountBalanceWallet />}
                            title="Posição patrimonial"
                            subtitle="Comparação entre a primeira e a última posição disponível"
                        />

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                width: "100%",
                            }}
                        >
                            {[
                                {
                                    label: "Ativo",
                                    data: balanco.variacoes?.ativo,
                                },
                                {
                                    label: "Passivo total",
                                    data: balanco.variacoes?.passivoTotal,
                                },
                                {
                                    label: "Patrimônio líquido",
                                    data: balanco.variacoes?.patrimonioLiquido,
                                },
                                {
                                    label: "Ativo circulante",
                                    data: balanco.variacoes?.ativoCirculante,
                                },
                                {
                                    label: "Passivo circulante",
                                    data: balanco.variacoes?.passivoCirculante,
                                },
                            ].map((item) => (
                                <Box
                                    key={item.label}
                                    sx={{
                                        flex: {
                                            xs: "1 1 100%",
                                            sm: "1 1 calc(50% - 8px)",
                                            md: "1 1 calc(33.333% - 11px)",
                                            lg: "1 1 calc(20% - 13px)",
                                        },
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: "12px",
                                            background: "#FAFAFC",
                                            border: `1px solid ${BORDER}`,
                                            height: "100%",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                color: MUTED,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                mt: 0.7,
                                                fontSize: "17px",
                                            }}
                                        >
                                            {formatCurrency(item.data?.atual)}
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={0.4}
                                            alignItems="center"
                                            sx={{
                                                mt: 0.5,
                                                color: getVariationColor(
                                                    item.data?.variacaoPercentual
                                                ),
                                                fontSize: "12px",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {getVariationIcon(
                                                item.data?.variacaoPercentual
                                            )}

                                            {formatPercent(
                                                Math.abs(
                                                    item.data?.variacaoPercentual || 0
                                                )
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                width: "100%",
                            }}
                        >
                            {/* Posição inicial */}
                            <Box
                                sx={{
                                    flex: {
                                        xs: "1 1 100%",
                                        md: "1 1 calc(33.333% - 11px)",
                                    },
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: "100%",
                                        p: 2.5,
                                        borderRadius: "16px",
                                        border: `1px solid ${BORDER}`,
                                        background: "#FFFFFF",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: MUTED,
                                            fontWeight: 700,
                                            mb: 0.5,
                                        }}
                                    >
                                        Posição inicial
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            fontWeight: 800,
                                            mb: 2.5,
                                        }}
                                    >
                                        {formatCompetencia(
                                            balanco.posicaoInicial?.competencia
                                        )}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: MUTED,
                                                }}
                                            >
                                                Ativo
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 800,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {formatCurrency(
                                                    balanco.posicaoInicial?.ativo
                                                )}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                height: "1px",
                                                backgroundColor: BORDER,
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: MUTED,
                                                }}
                                            >
                                                Patrimônio líquido
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 800,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {formatCurrency(
                                                    balanco.posicaoInicial
                                                        ?.patrimonioLiquido
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Posição final */}
                            <Box
                                sx={{
                                    flex: {
                                        xs: "1 1 100%",
                                        md: "1 1 calc(33.333% - 11px)",
                                    },
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: "100%",
                                        p: 2.5,
                                        borderRadius: "16px",
                                        border: `1px solid ${BORDER}`,
                                        background: "#FFFFFF",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: MUTED,
                                            fontWeight: 700,
                                            mb: 0.5,
                                        }}
                                    >
                                        Posição final
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            fontWeight: 800,
                                            mb: 2.5,
                                        }}
                                    >
                                        {formatCompetencia(
                                            balanco.posicaoFinal?.competencia
                                        )}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: MUTED,
                                                }}
                                            >
                                                Ativo
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 800,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {formatCurrency(
                                                    balanco.posicaoFinal?.ativo
                                                )}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                height: "1px",
                                                backgroundColor: BORDER,
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: MUTED,
                                                }}
                                            >
                                                Patrimônio líquido
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 800,
                                                    textAlign: "right",
                                                }}
                                            >
                                                {formatCurrency(
                                                    balanco.posicaoFinal
                                                        ?.patrimonioLiquido
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Metodologia */}
                            <Box
                                sx={{
                                    flex: {
                                        xs: "1 1 100%",
                                        md: "1 1 calc(33.333% - 11px)",
                                    },
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: "100%",
                                        p: 2.5,
                                        borderRadius: "16px",
                                        border: `1px solid ${BORDER}`,
                                        background: "#FAFAFC",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: MUTED,
                                            fontWeight: 700,
                                            mb: 1,
                                        }}
                                    >
                                        Metodologia
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            color: MUTED,
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        {balanco.metodologia}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    {/* DESPESAS */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`
                        }}
                    >
                        <SectionTitle
                            icon={<TrendingDown />}
                            title="Despesas"
                            subtitle="Distribuição das despesas classificadas"
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 2
                            }}
                        >
                            <Typography sx={{ color: MUTED }}>
                                Total identificado
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "22px",
                                    fontWeight: 850
                                }}
                            >
                                {formatCurrency(despesas.total)}
                            </Typography>
                        </Box>

                        <Stack spacing={1.5}>
                            {(despesas.porCategoria || []).map((item) => (
                                <Box key={item.categoria}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        sx={{ mb: 0.6 }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                fontWeight: 700
                                            }}
                                        >
                                            {translateConcept(item.categoria)}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                fontWeight: 800
                                            }}
                                        >
                                            {formatCurrency(item.valor)}
                                        </Typography>
                                    </Stack>

                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(
                                            item.percentualDespesas || 0,
                                            100
                                        )}
                                        sx={{
                                            height: 7,
                                            borderRadius: 5,
                                            background: "#EEEEF2",
                                            "& .MuiLinearProgress-bar": {
                                                background: PURPLE,
                                                borderRadius: 5
                                            }
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            fontSize: "11px",
                                            color: MUTED
                                        }}
                                    >
                                        {formatPercent(
                                            item.percentualDespesas
                                        )}{" "}
                                        das despesas ·{" "}
                                        {formatPercent(
                                            item.percentualReceita
                                        )}{" "}
                                        da receita
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>

                    {/* CONFERÊNCIAS */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`
                        }}
                    >
                        <SectionTitle
                            icon={<CheckCircle />}
                            title="Conferência dos documentos"
                            subtitle="Validações realizadas durante o processamento"
                        />

                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                            <Grid item xs={6} md={3}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        background: "#EAF8F0"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: "#087A3D"
                                        }}
                                    >
                                        ARQUIVOS
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "24px",
                                            fontWeight: 850,
                                            color: "#087A3D"
                                        }}
                                    >
                                        {conferencias.resumo?.arquivosProcessados ??
                                            0}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        background: "#F5F0FA"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: PURPLE
                                        }}
                                    >
                                        VERIFICAÇÕES OK
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "24px",
                                            fontWeight: 850,
                                            color: PURPLE
                                        }}
                                    >
                                        {totalVerificacoes}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        background: "#EAF8F0"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: "#087A3D"
                                        }}
                                    >
                                        DIVERGÊNCIAS
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "24px",
                                            fontWeight: 850,
                                            color: "#087A3D"
                                        }}
                                    >
                                        {divergencias}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        background: "#F5F5F7"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: MUTED
                                        }}
                                    >
                                        PÁGINAS
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "24px",
                                            fontWeight: 850
                                        }}
                                    >
                                        {conferencias.resumo?.totalPaginas ?? 0}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {(conferencias.cruzamentos || []).map((item) => (
                            <Box
                                key={item.codigo}
                                sx={{
                                    py: 1.4,
                                    borderBottom: `1px solid ${BORDER}`
                                }}
                            >
                                <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row"
                                    }}
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "13px"
                                            }}
                                        >
                                            {translateConcept(item.codigo)}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "11px",
                                                color: MUTED,
                                                mt: 0.3
                                            }}
                                        >
                                            {item.descricao}
                                        </Typography>
                                    </Box>

                                    <StatusChip status={item.situacao} />
                                </Stack>
                            </Box>
                        ))}
                    </Paper>

                    {/* ALERTAS */}
                    {alertas.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                borderRadius: "18px",
                                border: `1px solid ${BORDER}`,
                                overflow: "hidden"
                            }}
                        >
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                                <SectionTitle
                                    icon={<WarningAmber />}
                                    title="Pontos de atenção"
                                    subtitle={`${alertas.length} observações encontradas durante a análise`}
                                />

                                <Stack spacing={1.2}>
                                    {alertas.map((alerta, index) => {
                                        const isWarning =
                                            alerta.severidade === "aviso";

                                        return (
                                            <Box
                                                key={`${alerta.codigo}-${index}`}
                                                sx={{
                                                    p: 1.7,
                                                    borderRadius: "12px",
                                                    border: `1px solid ${isWarning
                                                        ? "#F1D58A"
                                                        : "#E4E7EC"
                                                        }`,
                                                    background:
                                                        isWarning
                                                            ? "#FFFBF0"
                                                            : "#FAFAFC"
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1.2}
                                                    alignItems="flex-start"
                                                >
                                                    {isWarning ? (
                                                        <WarningAmber
                                                            sx={{
                                                                color: "#C68A00",
                                                                mt: 0.2
                                                            }}
                                                        />
                                                    ) : (
                                                        <InfoOutlined
                                                            sx={{
                                                                color: MUTED,
                                                                mt: 0.2
                                                            }}
                                                        />
                                                    )}

                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 800,
                                                                fontSize: "13px"
                                                            }}
                                                        >
                                                            {alerta.mensagem}
                                                        </Typography>

                                                        {alerta.arquivo && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "11px",
                                                                    color: MUTED,
                                                                    mt: 0.4
                                                                }}
                                                            >
                                                                Documento:{" "}
                                                                {
                                                                    alerta.arquivo
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Paper>
                    )}

                    {/* DOCUMENTOS */}
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`,
                            overflow: "hidden"
                        }}
                    >
                        <Accordion
                            elevation={0}
                            sx={{
                                "&:before": {
                                    display: "none"
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{ px: 2.5 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.2}
                                    alignItems="center"
                                >
                                    <Description
                                        sx={{ color: PURPLE }}
                                    />

                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "15px"
                                            }}
                                        >
                                            Documentos utilizados
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: MUTED,
                                                fontSize: "11px"
                                            }}
                                        >
                                            {
                                                (
                                                    analise?.referencias
                                                        ?.documentos || []
                                                ).length
                                            }{" "}
                                            documentos considerados
                                        </Typography>
                                    </Box>
                                </Stack>
                            </AccordionSummary>

                            <AccordionDetails sx={{ px: 2.5 }}>
                                <Stack spacing={1}>
                                    {(
                                        analise?.referencias?.documentos || []
                                    ).map((doc) => (
                                        <Box
                                            key={doc.demonstracaoId}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: "10px",
                                                background: "#FAFAFC",
                                                border: `1px solid ${BORDER}`
                                            }}
                                        >
                                            <Stack
                                                direction={{
                                                    xs: "column",
                                                    sm: "row"
                                                }}
                                                justifyContent="space-between"
                                                spacing={1}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: "13px"
                                                        }}
                                                    >
                                                        {doc.arquivo}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: "11px",
                                                            color: MUTED,
                                                            mt: 0.3
                                                        }}
                                                    >
                                                        {doc.tipo}
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    size="small"
                                                    label={formatCompetencia(
                                                        doc.competencia
                                                    )}
                                                    sx={{
                                                        fontWeight: 700,
                                                        background: "#F4ECF7",
                                                        color: PURPLE
                                                    }}
                                                />
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>

                    {/* CAPACIDADE DA ANÁLISE */}
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            borderRadius: "18px",
                            border: `1px solid ${BORDER}`,
                            overflow: "hidden"
                        }}
                    >
                        <Accordion
                            elevation={0}
                            sx={{
                                "&:before": {
                                    display: "none"
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{ px: 2.5 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.2}
                                    alignItems="center"
                                >
                                    <Assessment
                                        sx={{ color: PURPLE }}
                                    />

                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "15px"
                                            }}
                                        >
                                            Recursos disponíveis
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: MUTED,
                                                fontSize: "11px"
                                            }}
                                        >
                                            Detalhes técnicos sobre a capacidade
                                            desta análise
                                        </Typography>
                                    </Box>
                                </Stack>
                            </AccordionSummary>

                            <AccordionDetails sx={{ px: 2.5 }}>
                                <Grid container spacing={1.5}>
                                    {Object.entries(
                                        capacidade.recursos || {}
                                    ).map(([key, resource]) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={key}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: "10px",
                                                    border: `1px solid ${BORDER}`
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {translateConcept(
                                                            key
                                                        )}
                                                    </Typography>

                                                    {resource.disponivel ? (
                                                        <CheckCircle
                                                            sx={{
                                                                fontSize: 17,
                                                                color: "#087A3D"
                                                            }}
                                                        />
                                                    ) : (
                                                        <ErrorOutline
                                                            sx={{
                                                                fontSize: 17,
                                                                color: "#98A2B3"
                                                            }}
                                                        />
                                                    )}
                                                </Stack>

                                                {!resource.disponivel &&
                                                    resource.motivo && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: "10px",
                                                                color: MUTED,
                                                                mt: 0.6
                                                            }}
                                                        >
                                                            {resource.motivo}
                                                        </Typography>
                                                    )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>

                    {/* RODAPÉ TÉCNICO */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                            px: 0.5
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "11px",
                                color: "#98A2B3"
                            }}
                        >
                            Análise ContBlack · Versão{" "}
                            {analise?.meta?.versao || "—"}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: "11px",
                                color: "#98A2B3"
                            }}
                        >
                            Gerada em {formatDate(analise?.meta?.geradoEm)}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        minHeight: "180px",
                        margin: "150px auto",
                        width: "80%",
                        px: 2,
                        borderRadius: "14px",
                        border: "1px dashed #E4E7EC",
                        backgroundColor: "#FAFAFA",
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(156, 1, 185, 0.08)",
                            color: "#9C01B9",
                            mb: 1.5,
                        }}
                    >
                        <AssessmentRounded
                            sx={{
                                fontSize: 22,
                            }}
                        />
                    </Box>

                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#344054",
                            mb: 0.5,
                        }}
                    >
                        Análise ainda não disponível
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "12px",
                            lineHeight: 1.6,
                            color: "#98A2B3",
                            maxWidth: "400px",
                        }}
                    >
                        Ainda não temos demonstrações contábeis suficientes para
                        gerar uma análise financeira da sua empresa.
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "11px",
                            color: "#667085",
                            mt: 1,
                        }}
                    >
                        Assim que novos documentos forem disponibilizados, sua análise
                        será atualizada automaticamente.
                    </Typography>
                </Box>
            )}

        </>
    );
}

